import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice.ts';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import type { SerializeForClipboardProps } from 'components/editor/utils/clipboard';
import { GridBlockType } from 'services/entities/BlockTypes';
import type { TableType } from 'components/editor/grid/reduxStore/table.types';
import type { VideoContent } from 'components/editor/videos/video';

let pasteCounter = 0;
let lastBlockId = '';

const isSignatureData = (data: BlockContent | SignatureBox): data is SignatureBox => {
  return data.type === GridBlockType.SIGNATURE;
};

const getBlockId = (data: BlockContent | SignatureBox): string | undefined => {
  return isSignatureData(data) ? data.signatureBoxId : data.blockConfig?.id;
};

const getPosition = (data: BlockContent | SignatureBox): { x: number | undefined; y: number | undefined } => {
  return isSignatureData(data)
    ? { x: data.properties.position.x, y: data.properties.position.y }
    : { x: data.blockConfig?.x, y: data.blockConfig?.y };
};

export const calculateRelativePosition = (clipboardData: SerializeForClipboardProps, activeSection?: string) => {
  const { data, sectionId: clipboardSectionId } = clipboardData;
  const position = getPosition(data as BlockContent | SignatureBox);
  const relativePosition = {
    x: position.x ?? 0,
    y: position.y ?? 0,
  };

  const isSameSection = clipboardSectionId === activeSection;

  const blockId = getBlockId(data as BlockContent | SignatureBox) ?? '';

  if (blockId !== lastBlockId) {
    pasteCounter = 0;
    lastBlockId = blockId;
  }

  const activeSectionCanvas = document.querySelector(`.canvas_${activeSection}`);

  if (!activeSectionCanvas) return relativePosition;

  const sectionRect = activeSectionCanvas.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  const topBoundary = -sectionRect.top;
  const bottomBoundary = viewportHeight - sectionRect.top;
  const isWithinVerticalBounds = relativePosition.y >= topBoundary && relativePosition.y <= bottomBoundary;
  const isBlockVisible = isSameSection && isWithinVerticalBounds;

  if (isBlockVisible) {
    relativePosition.x += 16 * (pasteCounter + 1);
    relativePosition.y += 16 * (pasteCounter + 1);
    pasteCounter++;
  } else {
    const visibleTop = Math.max(0, -sectionRect.top);
    const visibleBottom = Math.min(sectionRect.height, viewportHeight - sectionRect.top);
    const visibleMiddle = visibleTop + (visibleBottom - visibleTop) / 2;

    relativePosition.y = visibleMiddle;
  }
  return relativePosition;
};

export const getContentOption = (pastedData: BlockContent): string | TableType | VideoContent => {
  switch (pastedData.type) {
    case GridBlockType.TABLE: {
      const tableObject = { ...pastedData.contentTable };
      delete tableObject?.selectedRow;
      return tableObject as TableType;
    }
    case GridBlockType.VIDEO:
      return pastedData.contentObject as VideoContent;
    default:
      return pastedData.content ?? '';
  }
};
